import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import type { CoverageFields } from '@/Network/Types/Responses/CoverageResponses'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import type { CoverageInput } from '@/Network/Types/Requests/CoverageRequests'

/**
 * Plantão "Ocupado sem identificação" — regras puras do front.
 *
 * O status é SEMPRE derivado (nunca gravado como enum): o backend novo manda
 * `coverageStatus` pronto; o antigo só tem "com médico" ou "sem médico". A regra
 * de dado inconsistente (flag + médico) é a do contrato: o médico vence.
 */
export const COVERAGE_STATUS_LABELS: Record<CoverageStatus, string> = {
  [CoverageStatus.OPEN]: 'Vaga aberta',
  [CoverageStatus.DOCTOR]: 'Com médico',
  [CoverageStatus.UNIDENTIFIED]: 'Ocupado s/ identificação'
}

export const COVERAGE_STATUS_SHORT_LABELS: Record<CoverageStatus, string> = {
  [CoverageStatus.OPEN]: 'Aberta',
  [CoverageStatus.DOCTOR]: 'Com médico',
  [CoverageStatus.UNIDENTIFIED]: 'Ocupado s/ ident.'
}

export const COVERAGE_MODE_LABELS: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]: 'Médico',
  [CoverageMode.UNIDENTIFIED]: 'Ocupado sem identificação',
  [CoverageMode.OPEN]: 'Deixar vaga aberta'
}

/**
 * Rótulos das ações de cobertura exibidos ao usuário (pré-visualização, tabela
 * expandida, histórico). Fonte única: nenhum código técnico (CREATE, TRANSFER,
 * UNIDENTIFIED, REMOVE, IGNORED) aparece na tela. Os valores enviados à API
 * continuam sendo os códigos — isto é só apresentação.
 */
export const COVERAGE_ACTION_LABELS = {
  ALLOCATE_DOCTOR: 'Alocar médico',
  IDENTIFY_DOCTOR: 'Identificar médico',
  TRANSFER_DOCTOR: 'Trocar médico',
  MARK_UNIDENTIFIED: 'Marcar ocupado s/ ident.',
  CHANGE_RESPONSIBLE: 'Trocar responsável',
  REMOVE_DOCTOR: 'Remover médico',
  RELEASE_OPEN: 'Liberar vaga',
  IGNORED: 'Ignorado',
  NONE: 'Sem mudança'
} as const

export type CoverageActionLabel =
  (typeof COVERAGE_ACTION_LABELS)[keyof typeof COVERAGE_ACTION_LABELS]

export const UNIDENTIFIED_COVERAGE_LABEL = 'Ocupado sem identificação'
export const COVERAGE_RESPONSIBLE_LABEL = 'Responsável pela cobertura'
export const COVERAGE_NOTE_LABEL = 'Observação da cobertura (opcional)'
export const COVERAGE_NOTE_MAX_LENGTH = 500

const VALID_STATUSES = new Set<string>(Object.values(CoverageStatus))

export type CoverageSource =
  | (CoverageFields & {
      doctorId?: number | null
      doctor?: { id?: number | null } | null
      opened?: boolean | null
    })
  | null
  | undefined

/** `true` quando o payload já vem do backend com a feature (tem `coverageStatus` válido). */
export function hasCoverageContract(source: CoverageSource): boolean {
  const status = source?.coverageStatus
  return typeof status === 'string' && VALID_STATUSES.has(status)
}

export function resolveCoverageStatus(source: CoverageSource): CoverageStatus {
  if (!source) return CoverageStatus.OPEN
  if (hasCoverageContract(source)) return source.coverageStatus as CoverageStatus
  if (source.doctor?.id ?? source.doctorId) return CoverageStatus.DOCTOR
  if (source.unregisteredDoctor === true) return CoverageStatus.UNIDENTIFIED
  // Grade legada: `opened=false` só existe com médico.
  if (source.opened === false) return CoverageStatus.DOCTOR
  return CoverageStatus.OPEN
}

export const isUnidentifiedCoverage = (source: CoverageSource): boolean =>
  resolveCoverageStatus(source) === CoverageStatus.UNIDENTIFIED

type ResponsibleSource =
  | (CoverageFields & { coverageResponsible?: { name?: string | null } | null })
  | null
  | undefined

export function getCoverageResponsibleName(source: ResponsibleSource): string | null {
  return source?.coverageResponsibleName ?? source?.coverageResponsible?.name ?? null
}

export interface ScheduleCoverageFlags {
  allowUnregisteredDoctor?: boolean
  unidentifiedCoverageFeatureEnabled?: boolean
}

/** Backend com a feature: o campo `allowUnregisteredDoctor` vem na escala (true OU false). */
export function scheduleSupportsCoverage(
  schedule: ScheduleCoverageFlags | null | undefined
): boolean {
  return !!schedule && typeof schedule.allowUnregisteredDoctor === 'boolean'
}

/** Pode MARCAR plantões como ocupados sem identificação nesta escala. */
export function canMarkUnidentified(schedule: ScheduleCoverageFlags | null | undefined): boolean {
  return (
    schedule?.allowUnregisteredDoctor === true &&
    schedule?.unidentifiedCoverageFeatureEnabled !== false
  )
}

/**
 * BE com o recurso, mas a escala não permite: o seletor mostra "Ocupado sem
 * identificação" DESATIVADO (com a explicação de onde ligar). BE antigo (campo
 * ausente) ou kill switch global desligado: o segmento continua oculto.
 */
export function isUnidentifiedLockedBySchedule(
  schedule: ScheduleCoverageFlags | null | undefined
): boolean {
  return (
    scheduleSupportsCoverage(schedule) &&
    schedule?.allowUnregisteredDoctor === false &&
    schedule?.unidentifiedCoverageFeatureEnabled !== false
  )
}

export function buildCoverageInput(params: {
  mode: CoverageMode
  doctorId: number | null | undefined
  responsibleId: number | null | undefined
  note: string | null | undefined
}): CoverageInput {
  if (params.mode === CoverageMode.UNIDENTIFIED) {
    const note = params.note?.trim()
    return {
      coverage: CoverageMode.UNIDENTIFIED,
      coverageResponsibleId: params.responsibleId ?? undefined,
      ...(note ? { coverageNote: note } : {})
    }
  }
  if (params.mode === CoverageMode.OPEN) return { coverage: CoverageMode.OPEN }
  return params.doctorId ? { coverage: CoverageMode.DOCTOR } : {}
}

export interface StationCoverageInfo {
  status: CoverageStatus
  hasContract: boolean
  responsibleId: number | null
  responsibleName: string | null
  note: string | null
}

type StationDetailsCoverageSource =
  | {
      doctorStation?:
        | (CoverageFields & {
            doctor?: { id?: number | null } | null
            coverageResponsible?: { id?: number; name?: string | null } | null
          })
        | null
    }
  | null
  | undefined

/** Estado da cobertura do `GET /stations/:id` (campos dentro de `doctorStation`). */
export function getStationCoverageInfo(details: StationDetailsCoverageSource): StationCoverageInfo {
  const ds = details?.doctorStation ?? null
  return {
    status: resolveCoverageStatus(ds),
    hasContract: hasCoverageContract(ds),
    responsibleId: ds?.coverageResponsibleId ?? ds?.coverageResponsible?.id ?? null,
    responsibleName: getCoverageResponsibleName(ds),
    note: ds?.coverageNote ?? null
  }
}
